/**
 * TemplateLibrary — Mockup Post Previews + Section Limits (spec 11+)
 * Mini-mockupy postow na kartach, modal z pelnym podgladem, max 3 items/sekcja
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Star, Target, Zap, X,
  Facebook, Instagram, ThumbsUp, MessageCircle, Share2, Globe,
  Calendar, Clock, Check
} from 'lucide-react';
import CardPremium from '@/components/CardPremium';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/utils/api';

/* ── Badge map ────────────────────────────────────────── */
const BADGE_MAP = {
  sprzedaż: { label: 'Sprzedaz', color: '#F47A3B', bg: '#FFF3EC' },
  zasieg:   { label: 'Zasięg',   color: '#3B82F6', bg: '#EFF6FF' },
  leady:    { label: 'Leady',    color: '#8B5CF6', bg: '#F5F3FF' },
  autopromocja: { label: 'Autopromocja', color: '#10B981', bg: '#F0FDF4' },
};

/* ── Template definitions ─────────────────────────────── */
const TEMPLATES_HODOWLA = [
  { id: 'szczenieta', name: 'Promocja szczeniat', desc: 'Pokaz dostepne szczenieta i zachec do kontaktu.', badge: 'sprzedaż' },
  { id: 'pies', name: 'Promocja psa', desc: 'Przedstaw psa i pokaz jego atuty.', badge: 'sprzedaż' },
  { id: 'hodowla', name: 'Promocja hodowli', desc: 'Pokaż swoją hodowlę i buduj zaufanie.', badge: 'zasieg' },
  { id: 'informacyjny', name: 'Post informacyjny', desc: 'Podziel się wiedzą i buduj pozycję eksperta.', badge: 'zasieg' },
  { id: 'wizerunkowy', name: 'Post wizerunkowy', desc: 'Pokaz codziennosc hodowli i buduj relacje.', badge: 'autopromocja' },
  { id: 'sezonowy', name: 'Promocja sezonowa', desc: 'Wykorzystaj okazje: swieta, wakacje, event.', badge: 'leady' },
];

const TEMPLATES_BIZNES = [
  { id: 'oferta', name: 'Promocja oferty', desc: 'Pokaż aktualną ofertę i zachęć do zakupu.', badge: 'sprzedaż' },
  { id: 'usluga', name: 'Promocja uslugi', desc: 'Przedstaw usluge i pokaz efekty.', badge: 'sprzedaż' },
  { id: 'firma', name: 'Post o firmie', desc: 'Opowiedz o firmie i buduj zaufanie.', badge: 'zasieg' },
  { id: 'sprzedażowy', name: 'Post sprzedażowy', desc: 'Bezposredni przekaz sprzedażowy z CTA.', badge: 'leady' },
  { id: 'sezonowy_biz', name: 'Promocja sezonowa', desc: 'Wykorzystaj okazje: swieta, event, trendy.', badge: 'leady' },
  { id: 'realizacja', name: 'Post z realizacja', desc: 'Pokaz efekt pracy i buduj wiarygodnosc.', badge: 'autopromocja' },
];

const ALL_TEMPLATES = [...TEMPLATES_HODOWLA, ...TEMPLATES_BIZNES];
const getTemplate = (id) => ALL_TEMPLATES.find(t => t.id === id);

/* ── Visual mockup data per template ──────────────────── */
const MOCKUP_DATA = {
  szczenieta: {
    gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 50%, #D97706 100%)',
    emoji: '',
    sampleHeadline: 'Dostepne szczenieta',
    sampleText: 'Mamy dla Ciebie wspaniałe szczenięta z certyfikatem. Zdrowe, zaszczepione, gotowe do odbioru. Sprawdź naszą ofertę!',
    sampleCta: 'Sprawdź szczenięta',
    channels: ['facebook', 'instagram'],
  },
  pies: {
    gradient: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 50%, #2563EB 100%)',
    emoji: '',
    sampleHeadline: 'Poznaj naszego championa',
    sampleText: 'Przedstawiamy naszego reproduktora — zdrowy, z pelnym rodowodem i doskonalym temperamentem. Idealny partner do hodowli.',
    sampleCta: 'Dowiedz się więcej',
    channels: ['facebook'],
  },
  hodowla: {
    gradient: 'linear-gradient(135deg, #34D399 0%, #10B981 50%, #059669 100%)',
    emoji: '',
    sampleHeadline: 'Nasza hodowla',
    sampleText: 'Od 10 lat hodujemy z pasja i odpowiedzialnoscia. Kazdy szczeniak opuszcza nas zdrowy, zsocjalizowany i przygotowany do nowego domu.',
    sampleCta: 'Odwiedz hodowle',
    channels: ['facebook', 'instagram'],
  },
  informacyjny: {
    gradient: 'linear-gradient(135deg, #2DD4BF 0%, #14B8A6 50%, #0D9488 100%)',
    emoji: '',
    sampleHeadline: 'Czy wiesz, ze...',
    sampleText: 'Prawidlowe zywienie szczeniecia w pierwszych miesiącach zycia ma kluczowe znaczenie dla jego zdrowia. Oto nasze sprawdzone wskazowki.',
    sampleCta: 'Czytaj więcej',
    channels: ['facebook'],
  },
  wizerunkowy: {
    gradient: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 50%, #7C3AED 100%)',
    emoji: '',
    sampleHeadline: 'Dzien w hodowli',
    sampleText: 'Poranny spacer, zabawa na trawie i czas na socjalizacje. Tak wyglada kazdy dzien u nas. Zobaczcie sami!',
    sampleCta: 'Zobacz więcej',
    channels: ['facebook', 'instagram'],
  },
  sezonowy: {
    gradient: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 50%, #E11D48 100%)',
    emoji: '',
    sampleHeadline: 'Swiateczna promocja',
    sampleText: 'Z okazji zbliżających sie swiat przygotowalismy specjalny pakiet powitalny dla nowych wlascicieli. Nie przegap okazji!',
    sampleCta: 'Sprawdź ofertę',
    channels: ['facebook', 'instagram'],
  },
  oferta: {
    gradient: 'linear-gradient(135deg, #FB923C 0%, #F97316 50%, #EA580C 100%)',
    emoji: '',
    sampleHeadline: 'Nasza najlepsza oferta',
    sampleText: 'Specjalna oferta na nasze najlepsze produkty. Tylko do konca tygodnia — nie przegap!',
    sampleCta: 'Kup teraz',
    channels: ['facebook', 'instagram'],
  },
  usluga: {
    gradient: 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 50%, #0284C7 100%)',
    emoji: '',
    sampleHeadline: 'Profesjonalna usluga',
    sampleText: 'Oferujemy profesjonalną usługę na najwyższym poziomie. Sprawdź, dlaczego nasi klienci wracają.',
    sampleCta: 'Umow sie',
    channels: ['facebook'],
  },
  firma: {
    gradient: 'linear-gradient(135deg, #4ADE80 0%, #22C55E 50%, #16A34A 100%)',
    emoji: '',
    sampleHeadline: 'O naszej firmie',
    sampleText: 'Jestesmy zespolem pasjonatow, ktorzy stawiaja na jakosc i zadowolenie klienta. Poznaj nasz zespol i wartosc.',
    sampleCta: 'Poznaj nas',
    channels: ['facebook', 'instagram'],
  },
  sprzedażowy: {
    gradient: 'linear-gradient(135deg, #F87171 0%, #EF4444 50%, #DC2626 100%)',
    emoji: '',
    sampleHeadline: 'Ostatnie sztuki!',
    sampleText: 'Nie czekaj — zostalo tylko kilka sztuk w promocyjnej cenie. Zamow teraz i odbierz rabat!',
    sampleCta: 'Zamow teraz',
    channels: ['facebook'],
  },
  sezonowy_biz: {
    gradient: 'linear-gradient(135deg, #E879F9 0%, #D946EF 50%, #C026D3 100%)',
    emoji: '',
    sampleHeadline: 'Sezonowa oferta',
    sampleText: 'Wykorzystaj sezonową okazję — przygotowaliśmy dla Ciebie specjalne ceny i pakiety. Sprawdź, zanim się skończy!',
    sampleCta: 'Zobacz ofertę',
    channels: ['facebook', 'instagram'],
  },
  realizacja: {
    gradient: 'linear-gradient(135deg, #818CF8 0%, #6366F1 50%, #4F46E5 100%)',
    emoji: '',
    sampleHeadline: 'Nasza realizacja',
    sampleText: 'Kolejny zadowolony klient! Zobacz efekty naszej pracy i przekonaj sie, dlaczego warto nam zaufac.',
    sampleCta: 'Zobacz portfolio',
    channels: ['facebook', 'instagram'],
  },
};

const SECTION_LIMIT = 3;

/* ── Mini Mockup Card (simulated social post) ──────────── */
function MiniMockupCard({ tpl, reason, badgeLabel, badgeIcon, onPreview, onSelect }) {
  const badge = BADGE_MAP[tpl.badge];
  const mockup = MOCKUP_DATA[tpl.id] || MOCKUP_DATA.szczenieta;
  const hasMultiChannel = mockup.channels.length > 1;

  return (
    <CardPremium
      key={tpl.id + (badgeLabel || '')}
      className="p-0 group overflow-hidden"
      active={!!badgeLabel}
      onClick={() => onPreview(tpl, reason)}
      data-testid={`template-${tpl.id}`}
    >
      {/* Simulated post image */}
      <div
        className="h-[100px] relative"
        style={{ background: mockup.gradient }}
      >
        {/* Channel icons */}
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
          {mockup.channels.includes('facebook') && (
            <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Facebook className="w-3 h-3 text-white" />
            </div>
          )}
          {mockup.channels.includes('instagram') && (
            <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Instagram className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
        {/* Sample headline overlay */}
        <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/50 to-transparent">
          <p className="text-[11px] font-semibold text-white leading-tight truncate">
            {mockup.sampleHeadline}
          </p>
        </div>
      </div>

      {/* Simulated post text preview */}
      <div className="px-3 pt-2 pb-1.5 bg-[#FAFBFC] border-b border-[#F0F1F3]">
        <p className="text-[10px] text-[#65676B] leading-relaxed line-clamp-2">
          {mockup.sampleText.slice(0, 80)}...
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex items-center gap-0.5">
            <ThumbsUp className="w-2.5 h-2.5 text-[#65676B]/50" />
            <span className="text-[9px] text-[#65676B]/50">24</span>
          </div>
          <span className="text-[9px] text-[#65676B]/50">3 komentarze</span>
        </div>
      </div>

      {/* Template info */}
      <div className="px-3.5 pt-2.5 pb-3.5">
        <div className="flex items-start justify-between gap-1.5 mb-1">
          <h3 className="text-[13px] font-bold text-zinc-900 group-hover:text-[#F47A3B] transition-colors leading-tight">
            {tpl.name}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            {badgeLabel && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap text-[#F47A3B] bg-[#F47A3B]/10 flex items-center gap-0.5">
                {badgeIcon} {badgeLabel}
              </span>
            )}
            <span
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
              style={{ color: badge.color, background: badge.bg }}
            >
              {badge.label}
            </span>
          </div>
        </div>
        <p className="text-[11px] text-zinc-500 leading-relaxed mb-1">{tpl.desc}</p>
        {reason && <p className="text-[10px] text-[#F47A3B] font-medium mb-1.5">{reason}</p>}
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F47A3B] group-hover:underline mt-0.5">
          Zobacz podglad <ArrowRight className="w-2.5 h-2.5" />
        </span>
      </div>
    </CardPremium>
  );
}

/* ── Preview Modal (realistic post mockup) ─────────────── */
function TemplatePreviewModal({ tpl, reason, onClose, onSelect }) {
  const badge = BADGE_MAP[tpl.badge];
  const mockup = MOCKUP_DATA[tpl.id] || MOCKUP_DATA.szczenieta;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="template-preview-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-2xl shadow-2xl max-w-[520px] w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[#F0F1F3]">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg"
              style={{ background: mockup.gradient }}
            />
            <div>
              <h3 className="text-[14px] font-bold text-zinc-900">{tpl.name}</h3>
              <div className="flex items-center gap-1.5">
                <span
                  className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ color: badge.color, background: badge.bg }}
                >
                  {badge.label}
                </span>
                {mockup.channels.map(ch => (
                  <span key={ch} className="text-[9px] text-zinc-400 capitalize">{ch}</span>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
            data-testid="preview-modal-close"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {/* Realistic Facebook post mockup */}
        <div className="mx-5 mt-4 rounded-xl border border-[#E4E6EB] overflow-hidden" data-testid="preview-post-mockup">
          {/* FB Header */}
          <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-2 bg-white">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ background: mockup.gradient }}
            >
              T
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-[#050505] leading-tight">Twoja firma</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[10px] text-[#65676B]">Sponsorowane</span>
                <span className="text-[10px] text-[#65676B]">&middot;</span>
                <Globe className="w-2.5 h-2.5 text-[#65676B]" />
              </div>
            </div>
          </div>

          {/* Post text */}
          <div className="px-3.5 pb-2">
            <p className="text-[12px] leading-[1.4] text-[#050505]">
              {mockup.sampleText}
            </p>
          </div>

          {/* Post image */}
          <div
            className="w-full h-[200px] relative"
            style={{ background: mockup.gradient }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-white/90 text-[15px] font-bold drop-shadow-sm">
                  {mockup.sampleHeadline}
                </p>
                <p className="text-white/70 text-[11px] mt-1">{tpl.desc}</p>
              </div>
            </div>
          </div>

          {/* CTA bar */}
          <div className="px-3.5 py-2 border-t border-[#E4E6EB] flex items-center justify-between bg-[#F0F2F5]">
            <div>
              <p className="text-[10px] text-[#65676B]">twojafirma.pl</p>
              <p className="text-[11px] font-semibold text-[#050505]">{mockup.sampleHeadline}</p>
            </div>
            <div className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-[#E4E6EB] text-[#050505]">
              {mockup.sampleCta}
            </div>
          </div>

          {/* Reactions */}
          <div className="px-3.5 py-1.5 border-t border-[#E4E6EB]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <div className="flex -space-x-1">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center bg-[#1877F2]">
                    <ThumbsUp className="w-2 h-2 text-white" />
                  </div>
                  <div className="w-4 h-4 rounded-full flex items-center justify-center bg-[#F0284A]">
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                    </svg>
                  </div>
                </div>
                <span className="text-[10px] ml-0.5 text-[#65676B]">24</span>
              </div>
              <span className="text-[10px] text-[#65676B]">3 komentarze &middot; 2 udostępnienia</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-3 border-t border-[#E4E6EB]">
            {[
              { icon: ThumbsUp, label: 'Lubie to' },
              { icon: MessageCircle, label: 'Komentarz' },
              { icon: Share2, label: 'Udostepnij' },
            ].map(({ icon: Ic, label }) => (
              <div key={label} className="flex items-center justify-center gap-1 py-2 text-[#65676B]">
                <Ic className="w-3 h-3" />
                <span className="text-[11px] font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Publication plan */}
        <div className="mx-5 mt-4 rounded-xl bg-[#FAFBFC] border border-[#F0F1F3] p-3.5" data-testid="preview-publication-plan">
          <p className="text-[12px] font-bold text-zinc-900 mb-2.5 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[#F47A3B]" />
            Plan publikacji
          </p>
          <div className="space-y-2">
            {[
              { text: 'Post na Facebooku + grafika reklamowa', done: true },
              { text: 'Publikacja 3x w tygodniu', done: true },
              { text: 'Optymalne godziny publikacji', done: true },
              ...(mockup.channels.includes('instagram') ? [{ text: 'Caption na Instagram z hashtagami', done: true }] : []),
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#10B981] flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
                <span className="text-[11px] text-zinc-500">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Reason from scoring */}
        {reason && (
          <div className="mx-5 mt-3 rounded-xl bg-[#FFF7ED] border border-[#F47A3B]/15 p-3">
            <p className="text-[11px] text-[#F47A3B] font-medium flex items-center gap-1.5">
              <Star className="w-3 h-3" />
              {reason}
            </p>
          </div>
        )}

        {/* CTA */}
        <div className="px-5 pt-4 pb-5">
          <Button
            onClick={() => { onSelect(tpl.id); onClose(); }}
            className="w-full rounded-xl h-11 text-white font-semibold bg-[#F47A3B] hover:bg-[#E06A2B] text-[13px]"
            data-testid="preview-start-promotion"
          >
            Zacznij promocję <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-[10px] text-zinc-400 text-center mt-2">
            System przygotuje gotowe materialy na podstawie Twojego profilu
          </p>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Main component ───────────────────────────────────── */
export default function TemplateLibrary({ profileType, onSelect, onBack }) {
  const templates = profileType === 'hodowla' ? TEMPLATES_HODOWLA : TEMPLATES_BIZNES;
  const [scoring, setScoring] = useState(null);
  const [previewData, setPreviewData] = useState(null); // { tpl, reason }
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/booster/recommended-templates`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setScoring(res.data)).catch(() => {});
  }, []);

  const recommended = (scoring?.recommended || []).slice(0, SECTION_LIMIT);
  const leadFocused = (scoring?.lead_focused || []).slice(0, SECTION_LIMIT);
  const goodForStart = (scoring?.good_for_start || []).slice(0, SECTION_LIMIT);
  const allScoredIds = new Set([
    ...recommended.map(r => r.id),
    ...leadFocused.map(r => r.id),
    ...goodForStart.map(r => r.id),
  ]);
  const rest = templates.filter(t => !allScoredIds.has(t.id)).slice(0, SECTION_LIMIT);

  const handlePreview = (tpl, reason) => {
    setPreviewData({ tpl, reason });
  };

  const renderSection = (title, icon, items, badgeLabel, badgeIcon) => {
    if (!items || items.length === 0) return null;
    const cards = items.map(item => {
      const tpl = getTemplate(item.id);
      if (!tpl) return null;
      return (
        <MiniMockupCard
          key={tpl.id + (badgeLabel || '')}
          tpl={tpl}
          reason={item.reason}
          badgeLabel={badgeLabel}
          badgeIcon={badgeIcon}
          onPreview={(t, r) => handlePreview(t, r)}
          onSelect={onSelect}
        />
      );
    }).filter(Boolean);
    if (cards.length === 0) return null;

    return (
      <div className="mb-5" key={title}>
        <p
          className="text-[12px] font-bold uppercase tracking-wide mb-2.5 flex items-center gap-1.5"
          style={{ color: icon ? '#F47A3B' : '#9AA0A6' }}
        >
          {icon} {title}
        </p>
        <div className={`grid gap-3 ${cards.length >= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {cards}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="template-library">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-zinc-500 transition-colors mb-4"
        data-testid="template-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Wróć
      </button>

      <h2 className="text-[18px] font-bold text-zinc-900 mb-1">Wybierz sposob promocji</h2>
      <p className="text-[13px] text-zinc-500 mb-5">Kliknij szablon, żeby zobaczyć podgląd posta.</p>

      {/* Dynamic sections from scoring API */}
      {scoring && (
        <>
          {renderSection('Polecane dla Ciebie', <Star className="w-3.5 h-3.5" />, recommended, 'Polecane', <Star className="w-2.5 h-2.5" />)}
          {renderSection('Najlepsze do zdobywania leadow', <Target className="w-3.5 h-3.5" />, leadFocused, 'Leady', <Target className="w-2.5 h-2.5" />)}
          {renderSection('Dobre na start', <Zap className="w-3.5 h-3.5" />, goodForStart, 'Na start', <Zap className="w-2.5 h-2.5" />)}
        </>
      )}

      {/* Remaining templates */}
      {rest.length > 0 && (
        <div data-testid="all-templates-section">
          <p className="text-[12px] font-semibold text-zinc-400 uppercase tracking-wide mb-2.5">
            Wszystkie szablony
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {rest.map(tpl => (
              <MiniMockupCard
                key={tpl.id}
                tpl={tpl}
                reason={null}
                badgeLabel={null}
                badgeIcon={null}
                onPreview={(t) => handlePreview(t, null)}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <AnimatePresence>
        {previewData && (
          <TemplatePreviewModal
            tpl={previewData.tpl}
            reason={previewData.reason}
            onClose={() => setPreviewData(null)}
            onSelect={onSelect}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
